import { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { API } from "../App";
import { Card, CardContent } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { MessageCircle, CheckCircle, AlertCircle } from "lucide-react";
import { toast } from "sonner";

const fmt = (n) => `$${Number(n || 0).toLocaleString("es-MX", { maximumFractionDigits: 2 })}`;

const CotizacionPublica = () => {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    axios.get(`${API}/cotizaciones/publica/${token}`)
      .then(r => setData(r.data))
      .catch(() => toast.error("Cotización no encontrada"))
      .finally(() => setLoading(false));
  }, [token]);

  const enviarWhatsapp = () => {
    if (!data) return;
    const s = data.snapshot;
    const c = data.datos_cliente || {};
    const msg = `Hola ${c.nombre || ""} ✨

Te comparto la cotización para tu evento.

Fecha: ${data.fecha_evento || ""}
Horario: ${data.horario || ""}
Tipo de evento: ${data.tipo_evento || ""}
Invitados: ${s.personas_registradas || 0} personas

Uso de instalaciones: ${fmt(s.subtotal_instalaciones)}
Menú: ${fmt(s.subtotal_menus)}
Extras: ${fmt(s.subtotal_extras)}

Total del evento: ${fmt(s.total)}
Anticipo para apartar fecha: ${fmt(s.anticipo)}
Saldo pendiente: ${fmt(s.saldo)}

Liga de tu cotización: ${window.location.href}`;
    const phone = (c.telefono || "").replace(/[^0-9]/g, "");
    const url = phone
      ? `https://wa.me/${phone}?text=${encodeURIComponent(msg)}`
      : `https://wa.me/?text=${encodeURIComponent(msg)}`;
    window.open(url, "_blank");
  };

  if (loading) {
    return <div className="min-h-screen flex items-center justify-center bg-stone-50"><div className="w-8 h-8 border-2 border-emerald-900 border-t-transparent rounded-full animate-spin" /></div>;
  }
  if (!data) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-stone-50 p-4">
        <Card className="max-w-md w-full"><CardContent className="p-6 text-center">
          <AlertCircle className="w-12 h-12 text-stone-300 mx-auto mb-3" />
          <p className="text-stone-700">Esta cotización no existe o ha sido eliminada.</p>
        </CardContent></Card>
      </div>
    );
  }

  const s = data.snapshot || {};
  const c = data.datos_cliente || {};

  return (
    <div className="min-h-screen bg-stone-50 pb-12" data-testid="cotizacion-publica">
      <header className="bg-emerald-900 text-stone-50 py-8 px-4 text-center">
        <h1 className="font-display text-2xl sm:text-3xl">Cotización del Evento</h1>
        {/* Iter131 · P2 · No hardcodear marcas de Celebra en la vista pública de cotización.
             Si el backend adjunta datos del proveedor los mostramos; si no, dejamos vacío. */}
        {(data.brand_name || data.proveedor_nombre) && (
          <p className="text-sm text-stone-200 mt-1">{data.brand_name || data.proveedor_nombre}</p>
        )}
      </header>

      <div className="max-w-2xl mx-auto px-4 pt-6 space-y-4">
        <Card className="border-emerald-200 bg-emerald-50">
          <CardContent className="p-4 flex items-center gap-3">
            <CheckCircle className="w-6 h-6 text-emerald-700 flex-shrink-0" />
            <div>
              <p className="font-medium text-emerald-900">Hola {c.nombre}, esta es tu cotización</p>
              <p className="text-xs text-emerald-700">Guarda este enlace para consultarla cuando quieras.</p>
            </div>
          </CardContent>
        </Card>

        <Card className="border-stone-200">
          <CardContent className="p-5 space-y-3">
            <div className="grid grid-cols-2 gap-3 text-sm">
              <div><p className="text-xs text-stone-500 uppercase">Fecha</p><p className="font-medium">{data.fecha_evento || "Por definir"}</p></div>
              <div><p className="text-xs text-stone-500 uppercase">Horario</p><p className="font-medium">{data.horario || "Por definir"}</p></div>
              <div><p className="text-xs text-stone-500 uppercase">Tipo evento</p><p className="font-medium">{data.tipo_evento}</p></div>
              <div><p className="text-xs text-stone-500 uppercase">Personas</p><p className="font-medium">{s.personas_registradas} ({s.personas_cobrables} cobrables)</p></div>
            </div>
          </CardContent>
        </Card>

        <Card className="border-stone-200">
          <CardContent className="p-5 space-y-2 text-sm">
            <h3 className="font-display text-lg text-stone-900 mb-2">Detalle</h3>
            {s.subtotal_instalaciones > 0 && (
              <div className="flex justify-between"><span>{s.instalaciones?.nombre || "Uso de instalaciones"}</span><span className="font-medium">{fmt(s.subtotal_instalaciones)}</span></div>
            )}
            {s.menu_adulto && s.subtotal_menu_adultos > 0 && (
              <div className="flex justify-between"><span>{s.menu_adulto.nombre}</span><span className="font-medium">{fmt(s.subtotal_menu_adultos)}</span></div>
            )}
            {s.menu_nino && s.subtotal_menu_ninos > 0 && (
              <div className="flex justify-between"><span>{s.menu_nino.nombre}</span><span className="font-medium">{fmt(s.subtotal_menu_ninos)}</span></div>
            )}
            {s.personas_faltantes > 0 && (
              <div className="flex justify-between text-amber-700"><span>Ajuste mínimo operativo ({s.personas_faltantes} personas)</span><span>{fmt(s.subtotal_minimo)}</span></div>
            )}
            {s.total_dj > 0 && (
              <div className="flex justify-between"><span>DJ {s.horas_extra_dj > 0 && `+ ${s.horas_extra_dj}h extra`}</span><span className="font-medium">{fmt(s.total_dj)}</span></div>
            )}
            {s.total_horas_extra_local > 0 && (
              <div className="flex justify-between"><span>{s.horas_extra_local}h extra de salón</span><span className="font-medium">{fmt(s.total_horas_extra_local)}</span></div>
            )}
            {(s.extras_detalle || []).map(e => (
              <div key={e.servicio_id} className="flex justify-between"><span>{e.nombre} {e.cantidad > 1 && `x${e.cantidad}`}</span><span className="font-medium">{fmt(e.importe)}</span></div>
            ))}
            <hr className="border-stone-300 my-2" />
            <div className="flex justify-between text-stone-600"><span>Subtotal</span><span>{fmt(s.subtotal_general)}</span></div>
            {s.iva_activo && s.iva_monto > 0 && (
              <div className="flex justify-between text-stone-600"><span>IVA ({s.iva_porcentaje}%)</span><span>{fmt(s.iva_monto)}</span></div>
            )}
            <div className="flex justify-between text-xl font-bold text-emerald-900 pt-2"><span>Total</span><span data-testid="total-publica">{fmt(s.total)}</span></div>
            <div className="flex justify-between text-sm pt-2 border-t border-stone-200"><span>Anticipo para apartar fecha</span><span className="font-semibold text-emerald-700">{fmt(s.anticipo)}</span></div>
            <div className="flex justify-between text-sm"><span>Saldo pendiente</span><span className="font-semibold">{fmt(s.saldo)}</span></div>
          </CardContent>
        </Card>

        {s.condiciones && (
          <p className="text-xs text-stone-500 italic px-2">{s.condiciones}</p>
        )}

        <Button onClick={enviarWhatsapp} className="w-full bg-emerald-700 hover:bg-emerald-800 text-white" data-testid="btn-whatsapp-publica">
          <MessageCircle className="w-4 h-4 mr-2" /> Compartir por WhatsApp
        </Button>
      </div>
    </div>
  );
};

export default CotizacionPublica;
